import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail, Edit2, CreditCard, CheckCircle, Send, XCircle, ExternalLink, Copy, Banknote } from "lucide-react";

const InvoiceViewPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: ""
  });

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      setInvoice(response.data);
      setEmailAddress(response.data.customer?.email || "");
    } catch (error) {
      toast.error("Failed to load invoice");
      navigate('/dashboard/invoices');
    } finally {
      setLoading(false);
    }
  };

  // Check for Stripe payment status on load
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId && currentOrg) {
      // Verify payment with backend
      const verifyPayment = async () => {
        try {
          const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`, {
            withCredentials: true,
            headers: { 'X-Organization-Id': currentOrg.org_id }
          });
          if (response.data.payment_status === 'paid') {
            toast.success("Payment successful!");
          }
        } catch (error) {
          console.error("Failed to verify payment:", error);
        }
        // Refresh invoice data
        fetchInvoice();
        // Clear URL params
        navigate(`/dashboard/invoices/${id}`, { replace: true });
      };
      verifyPayment();
    } else if (paymentStatus === 'cancelled') {
      toast.info("Payment was cancelled");
      navigate(`/dashboard/invoices/${id}`, { replace: true });
    }
  }, [searchParams, currentOrg]);

  useEffect(() => {
    if (currentOrg) {
      fetchInvoice();
    }
  }, [currentOrg, id]);

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/invoices/${id}/send-email`, { email: emailAddress }, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Invoice sent successfully");
      setEmailDialogOpen(false);
      await fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.post(`${API}/invoices/${id}/status`, { status: newStatus }, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Status updated");
      await fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        invoice_id: id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes
      }, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Payment recorded");
      setPaymentDialogOpen(false);
      setPaymentForm({ amount: "", payment_method: "cash", reference_number: "", notes: "" });
      await fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  // Stripe payment
  const handleStripePayment = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/stripe/create-session`, {
        invoice_id: id,
        origin_url: window.location.origin
      }, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
      setProcessingPayment(false);
    }
  };

  // Generate payment link for customer
  const handleGeneratePaymentLink = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/payment-link`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      const link = `${window.location.origin}/pay/${response.data.payment_token}`;
      setPaymentLink(link);
      setPaymentLinkDialogOpen(true);
    } catch (error) {
      toast.error("Failed to generate payment link");
    }
  };

  const copyPaymentLink = () => {
    navigator.clipboard.writeText(paymentLink);
    toast.success("Payment link copied!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!invoice) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'sent': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'draft': return 'bg-zinc-100 text-zinc-700 border-zinc-200';
      case 'partially_paid': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-zinc-100 text-zinc-700 border-zinc-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="invoice-view-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-outfit text-2xl font-medium text-zinc-900">
              Invoice {invoice.invoice_number}
            </h1>
            <span className={`inline-flex px-3 py-1 text-sm font-medium border ${getStatusColor(invoice.status)}`}>
              {invoice.status?.replace('_', ' ')}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} className="gap-2" data-testid="download-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)} className="gap-2" data-testid="send-email-btn">
            <Mail className="w-4 h-4" /> Send
          </Button>
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <>
              <Button variant="outline" size="sm" onClick={handleGeneratePaymentLink} className="gap-2" data-testid="payment-link-btn">
                <ExternalLink className="w-4 h-4" /> Payment Link
              </Button>
              <Link to={`/dashboard/invoices/${id}/edit`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <Edit2 className="w-4 h-4" /> Edit
                </Button>
              </Link>
              <Button size="sm" onClick={() => setPaymentDialogOpen(true)} className="gap-2" data-testid="record-payment-btn">
                <Banknote className="w-4 h-4" /> Record Payment
              </Button>
              <Button size="sm" onClick={handleStripePayment} disabled={processingPayment} className="gap-2 bg-[#635bff] hover:bg-[#5249e0]" data-testid="stripe-pay-btn">
                <CreditCard className="w-4 h-4" /> {processingPayment ? "Processing..." : "Pay with Card"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Status Actions */}
      {invoice.status === 'draft' && (
        <div className="bg-blue-50 border border-blue-200 p-4 flex items-center justify-between">
          <p className="text-sm text-blue-700">This invoice is a draft and hasn't been sent yet.</p>
          <Button size="sm" onClick={() => handleStatusChange('sent')} className="gap-2">
            <Send className="w-4 h-4" /> Mark as Sent
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Invoice Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Dates */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xs uppercase text-muted-foreground font-medium mb-2">Bill To</h3>
                <p className="font-medium">{invoice.customer?.business_name}</p>
                <p className="text-sm text-muted-foreground">{invoice.customer?.billing_address}</p>
                <p className="text-sm text-muted-foreground">{invoice.customer?.email}</p>
              </div>
              <div className="text-right">
                <div className="space-y-1 text-sm">
                  <p><span className="text-muted-foreground">Invoice Date:</span> {invoice.invoice_date ? new Date(invoice.invoice_date + 'T00:00:00').toLocaleDateString('en-US') : ''}</p>
                  <p><span className="text-muted-foreground">Due Date:</span> {invoice.due_date ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString('en-US') : ''}</p>
                  <p><span className="text-muted-foreground">Currency:</span> {invoice.currency}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Item</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Qty</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Price</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Tax</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items?.map((item, index) => (
                  <tr key={index} className="border-b border-zinc-100">
                    <td className="py-3 px-4">
                      <p className="font-medium">{item.item_name}</p>
                      {item.item_upc && <p className="text-xs text-muted-foreground">UPC: {item.item_upc}</p>}
                    </td>
                    <td className="py-3 px-4 text-right">{item.quantity} {item.unit_type}</td>
                    <td className="py-3 px-4 text-right">${item.unit_price?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">${item.tax_amount?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-medium">${item.line_total?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-4 bg-zinc-50">
              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${invoice.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span>${invoice.tax_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-medium pt-2 border-t border-zinc-200">
                    <span>Total</span>
                    <span>${invoice.grand_total?.toFixed(2)}</span>
                  </div>
                  {invoice.amount_paid > 0 && (
                    <>
                      <div className="flex justify-between text-emerald-600">
                        <span>Paid</span>
                        <span>-${invoice.amount_paid?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-medium text-primary">
                        <span>Balance Due</span>
                        <span>${invoice.balance_due?.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {(invoice.notes || invoice.terms) && (
            <div className="bg-white border border-zinc-200 p-6 space-y-4">
              {invoice.notes && (
                <div>
                  <h3 className="text-xs uppercase text-muted-foreground font-medium mb-2">Notes</h3>
                  <p className="text-sm">{invoice.notes}</p>
                </div>
              )}
              {invoice.terms && (
                <div>
                  <h3 className="text-xs uppercase text-muted-foreground font-medium mb-2">Terms & Conditions</h3>
                  <p className="text-sm">{invoice.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-white border border-zinc-200 p-6">
            <h3 className="font-outfit font-medium mb-4">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Template</span>
                <span className="font-medium capitalize">{invoice.template || 'Modern'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium">${invoice.grand_total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Paid</span>
                <span className="text-emerald-600">${invoice.amount_paid?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-zinc-200">
                <span className="font-medium">Balance Due</span>
                <span className={`font-medium ${invoice.balance_due > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  ${invoice.balance_due?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className="bg-white border border-zinc-200 p-6">
            <h3 className="font-outfit font-medium mb-4">Payments</h3>
            {invoice.payments?.length > 0 ? (
              <div className="space-y-3">
                {invoice.payments.map((payment) => (
                  <div key={payment.payment_id} className="flex items-center justify-between p-3 bg-zinc-50 text-sm">
                    <div>
                      <p className="font-medium">${payment.amount?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{payment.payment_date} • {payment.payment_method}</p>
                    </div>
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No payments recorded</p>
            )}
          </div>

          {/* Quick Actions */}
          {invoice.status !== 'cancelled' && (
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="font-outfit font-medium mb-4">Quick Actions</h3>
              <div className="space-y-2">
                {invoice.status === 'sent' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start gap-2"
                    onClick={() => handleStatusChange('paid')}
                  >
                    <CheckCircle className="w-4 h-4" /> Mark as Paid
                  </Button>
                )}
                {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start gap-2 text-destructive"
                    onClick={() => handleStatusChange('cancelled')}
                  >
                    <XCircle className="w-4 h-4" /> Cancel Invoice
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                placeholder={`Max: $${invoice.balance_due?.toFixed(2)}`}
                required
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Credit/Debit Card</SelectItem>
                  <SelectItem value="ach">ACH Bank Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                placeholder="Transaction ID, check number, etc."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Optional payment notes"
                data-testid="payment-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-payment-btn">Record Payment</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="customer@example.com"
                data-testid="email-address-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>Cancel</Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail} data-testid="confirm-send-email-btn">
                {sendingEmail ? "Sending..." : "Send Invoice"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog open={paymentLinkDialogOpen} onOpenChange={setPaymentLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Payment Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share this link with your customer so they can pay online with card.
            </p>
            <div className="flex gap-2">
              <Input
                value={paymentLink}
                readOnly
                className="font-mono text-sm"
                data-testid="payment-link-input"
              />
              <Button onClick={copyPaymentLink} variant="outline" className="shrink-0" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
              <p><strong>Payment Methods Available:</strong></p>
              <ul className="list-disc ml-4 mt-1">
                <li>Credit/Debit Card (via Stripe)</li>
                <li>Cash, Check, ACH — contact info shown</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPaymentLinkDialogOpen(false)}>Close</Button>
              <Button onClick={() => window.open(paymentLink, '_blank')} className="gap-2">
                <ExternalLink className="w-4 h-4" /> Preview Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceViewPage;
